import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "motion/react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { AiChat } from "@src/features/ai/components/AiChat/AiChat";
import { useAtlasChatLayout } from "@src/features/ai/components/AiChat/useAtlasChatLayout";
import { useAtlasRailCollapse } from "@src/features/ai/components/AiChat/useAtlasRailCollapse";
import { usesAtlasCreationRail } from "@src/features/ai/utils/atlasCreationPhase";
import { useRedirectOnCategorySwitch } from "@src/features/experiments/hooks/use-redirect-on-category-switch";
import { useSelector } from "@src/store";
import {
  getAiChatProps,
  getAtlasCreationPhase,
  getIsAiChatOpen,
  getIsShellRailHost,
} from "@src/store/slices/global";

import { AlertBannerProvider } from "./AlertBannerProvider";
import type { AppLayoutShellProps } from "./AppLayoutShell.types";
import {
  RootPageTopBarContext,
  rootPageViewportHeight,
} from "./RootPageTopBar";
import { useAlertBannerHeight } from "./useAlertBanner";

export type {
  AppLayoutShellMaxWidth,
  AppLayoutShellProps,
} from "./AppLayoutShell.types";

const MotionStack = motion.create(Stack);
const HANDOFF_EASE = [0.2, 0, 0, 1] as const;
const ENTER_EASE = [0, 0, 0.2, 1] as const;

/**
 * Shared desktop shell for logged-in page providers.
 *
 * Guarantees:
 * - Optional `banner` and `sideNav` slots.
 * - Content width modes: centered `1600` container or full-width (`none`).
 * - Main scrolling remains document-level (no additional nested scroll regions).
 *
 * Provider-specific state and behavior should remain in the provider and be passed in via
 * slots/children.
 */
export const AppLayoutShell = ({
  banner,
  sideNav,
  maxWidth = 1600,
  children,
}: AppLayoutShellProps) => {
  const isAiChatOpen = useSelector(getIsAiChatOpen);
  // A full-page dialog can claim the rail (it is portalled above this shell, so
  // a rail rendered here would sit underneath it). Only one `AiChat` may be
  // mounted at a time — see `AiChat.railHost`.
  const isShellRailHost = useSelector(getIsShellRailHost);
  const showsChat = isAiChatOpen && isShellRailHost;
  // While Atlas is still asking what a new rule should do there is no page to
  // show beside it: the conversation takes the whole work area. Only the
  // frame changes — `<main>` is hidden rather than unmounted and the rail
  // keeps its place in the tree — so neither the page nor the ONE chat
  // instance remounts across the switch to the docked rail.
  const isAtlasFullScreen = useAtlasChatLayout() === "full" && showsChat;
  const atlasPhase = useSelector(getAtlasCreationPhase);
  const isAtlasHandoff =
    showsChat && (isAtlasFullScreen || atlasPhase === "building");
  const { mode, initialContext, createdRuleId } = useSelector(getAiChatProps);
  const [railWidth, setRailWidth] = useState(
    usesAtlasCreationRail({
      mode,
      layout: isAtlasFullScreen ? "full" : "docked",
      initialContext,
      createdRuleId,
    })
      ? 420
      : 555
  );
  const reducedMotion = useReducedMotion();
  const [stageNode, setStageNode] = useState<HTMLElement | null>(null);
  const [stageWidth, setStageWidth] = useState(0);
  useEffect(() => {
    if (!stageNode) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        setStageWidth(entry.contentRect.width);
      }
    });
    observer.observe(stageNode);
    setStageWidth(stageNode.clientWidth);
    return () => observer.disconnect();
  }, [stageNode]);
  // The merchant hid the rail on a rule (its minus; the page's top bar brings
  // it back). Hidden, not unmounted, for the same reason as `<main>` above:
  // the conversation must come back exactly as it was left.
  const isAtlasRailHidden = useAtlasRailCollapse().hidden;
  // DOM node in state, so `useState` rather than immer (drafts reject DOM
  // types). The slot's height feeds the chat rail's sticky offset, so the
  // rail starts below the bar a root page portals in — and collapses back to
  // the full viewport when the slot is empty.
  const [topBarNode, setTopBarNode] = useState<HTMLElement | null>(null);
  const [topBarHeight, setTopBarHeight] = useState(0);
  useEffect(() => {
    if (!topBarNode) {
      setTopBarHeight(0);
      return;
    }
    const observer = new ResizeObserver(() => {
      setTopBarHeight(topBarNode.offsetHeight);
    });
    observer.observe(topBarNode);
    return () => observer.disconnect();
  }, [topBarNode]);
  // Swaps route when the AI assistant flips category (experiment <->
  // personalization) on the experience currently on screen — the two
  // categories are separate route trees with no shared parent that would
  // otherwise pick up the change. Mounted here (not inside either route) so
  // it survives the swap and the open chat isn't disturbed by it.
  useRedirectOnCategorySwitch();
  const contentWidthStyles =
    maxWidth === 1600
      ? {
          maxWidth: "1600px",
          marginLeft: "auto",
          marginRight: "auto",
          width: "100%",
        }
      : {};

  return (
    <AlertBannerProvider>
      <Stack sx={{ minHeight: "100vh" }}>
        {banner ? <Box component="header">{banner}</Box> : null}
        <Stack direction="row" sx={{ flex: 1 }}>
          {sideNav ? (
            <Box
              component="nav"
              aria-label="Primary navigation"
              sx={{
                flexShrink: 0,
                position: "sticky",
                top: 0,
                height: "100dvh",
                overflowY: "auto",
                alignSelf: "flex-start",
              }}
            >
              {sideNav}
            </Box>
          ) : null}
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            {/* Full-width top-bar slot: a root page (`IgRootPageWrapper`)
                portals its breadcrumbs/actions row here so the row spans the
                whole layout — including over the AI chat rail below. Empty
                (zero-height) on every other page. */}
            <Box ref={setTopBarNode} sx={{ flexShrink: 0 }} />
            <Stack
              ref={setStageNode}
              direction="row"
              sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}
            >
              <MotionStack
                component="main"
                id="main-content"
                aria-hidden={isAtlasFullScreen || undefined}
                initial={false}
                animate={{
                  clipPath: isAtlasFullScreen
                    ? "inset(0 100% 0 0)"
                    : "inset(0 0% 0 0)",
                  opacity: isAtlasFullScreen ? 0 : 1,
                }}
                transition={
                  reducedMotion || !isAtlasHandoff
                    ? { duration: 0 }
                    : {
                        clipPath: { duration: 0.52, ease: HANDOFF_EASE },
                        opacity: {
                          duration: 0.28,
                          delay: isAtlasFullScreen ? 0 : 0.12,
                          ease: ENTER_EASE,
                        },
                      }
                }
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: isAtlasHandoff ? "hidden" : undefined,
                  position: "relative",
                  pointerEvents: isAtlasFullScreen ? "none" : "auto",
                  visibility: isAtlasFullScreen ? "hidden" : "visible",
                  ...contentWidthStyles,
                }}
              >
                <RootPageTopBarContext.Provider
                  value={{
                    node: topBarNode,
                    height: topBarHeight,
                    besideChatRail:
                      showsChat && !isAtlasFullScreen && !isAtlasRailHidden,
                  }}
                >
                  {children}
                </RootPageTopBarContext.Provider>
              </MotionStack>
              {showsChat ? (
                <ShellChatRail
                  topBarHeight={topBarHeight}
                  fill={isAtlasFullScreen}
                  hidden={isAtlasRailHidden}
                  handoff={isAtlasHandoff}
                  stageWidth={stageWidth}
                  railWidth={railWidth}
                  onPanelWidthChange={setRailWidth}
                />
              ) : null}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </AlertBannerProvider>
  );
};

/**
 * The chat rail beside `<main>`. Rendered inside `AlertBannerProvider` so it
 * can read the banner's height.
 *
 * Beside a root page — which portals its bar into the top-bar slot, so the
 * slot has height — the rail takes the page's own height, banner and all:
 * the document does not scroll there, so a taller rail would scroll it. On
 * every other page the document scrolls, the banner scrolls away with it, and
 * the sticky rail keeps the full viewport.
 */
const ShellChatRail = ({
  topBarHeight,
  fill,
  hidden,
  handoff,
  stageWidth,
  railWidth,
  onPanelWidthChange,
}: {
  topBarHeight: number;
  /** Atlas is full screen (see `useAtlasChatLayout`). */
  fill: boolean;
  /** The merchant hid the rail (see `useAtlasRailCollapse`). */
  hidden: boolean;
  handoff: boolean;
  stageWidth: number;
  railWidth: number;
  onPanelWidthChange: (width: number) => void;
}) => {
  const alertBannerHeight = useAlertBannerHeight();
  const isBesideRootPage = topBarHeight > 0;
  const reducedMotion = useReducedMotion();

  return (
    <motion.aside
      aria-label="AI chat"
      initial={false}
      animate={{ width: fill ? stageWidth || "100%" : railWidth }}
      transition={
        reducedMotion || !handoff
          ? { duration: 0 }
          : { duration: 0.52, ease: HANDOFF_EASE }
      }
      style={{
        flexShrink: 0,
        display: hidden ? "none" : undefined,
        position: "sticky",
        top: topBarHeight,
        height: isBesideRootPage
          ? rootPageViewportHeight({ alertBannerHeight, topBarHeight })
          : "100dvh",
        alignSelf: "flex-start",
      }}
    >
      <AiChat onPanelWidthChange={onPanelWidthChange} />
    </motion.aside>
  );
};
